import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MapPin, ImagePlus, Save, Search, AlertTriangle, CalendarClock, X, Plus, Trash2 } from "lucide-react";
import { useLang } from "@/lib/lang";

/**
 * Partner Portal → Catálogo page
 *
 * Lets the partner:
 *   1. Set their logo (base64, applied on the public /book/[slug] widget)
 *   2. Enter/edit their postal code — server derives the zone
 *   3. Pick which tours / fixed-routes to offer + custom markup % per item
 * Items whose zone doesn't match the partner's zone are flagged: they can still
 * be enabled but will be sold under "private driver" pricing at the widget.
 */
export default function PartnerCatalog() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [rulesEditor, setRulesEditor] = useState(null); // { kind: 'tour'|'route', id, name, rules: [...] }
  const [previewDate, setPreviewDate] = useState("");

  const computeEffective = (item, base) => {
    // Priority: seasonal (if previewDate matches) > price_override > markup_pct > base
    const rules = item.seasonal_rules || [];
    if (previewDate && rules.length > 0) {
      const rule = rules.find((r) => r.date_from && r.date_to && r.date_from <= previewDate && previewDate <= r.date_to);
      if (rule) {
        const val = Number(rule.value) || 0;
        if (rule.mode === "price_override" && val > 0) return { price: val, source: "seasonal_override", rule };
        if (rule.mode === "markup_pct" && val > 0) return { price: base * (1 + val / 100), source: "seasonal_markup", rule };
      }
    }
    const override = Number(item.price_override) || 0;
    if (override > 0) return { price: override, source: "override", rule: null };
    const markup = Number(item.markup_pct) || 0;
    if (markup > 0) return { price: base * (1 + markup / 100), source: "markup", rule: null };
    return { price: base, source: "base", rule: null };
  };

  const load = async () => {
    const [meResp, catResp, catsResp] = await Promise.all([
      api.get("/partner/me"),
      api.get("/partner/catalog"),
      api.get("/public/vehicle-categories-v2"),  // public endpoint
    ]);
    setMe(meResp.data);
    // Iter 25 Sub-Fase D — normaliza transferes: cria uma linha por cada categoria activa,
    // preservando as escolhas do parceiro (transfers array vem de catalog do backend).
    const partnerTransfers = (catResp.data.transfers || []);
    const activeCats = (catsResp.data || []).filter((c) => c.active).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const transferRows = activeCats.map((c) => {
      const existing = partnerTransfers.find((t) => t.id === c.key);
      return {
        id: c.key,
        label: c.label,
        base_price_eur: c.base_price_eur,
        price_per_km: c.price_per_km,
        price_per_minute: c.price_per_minute,
        capacity_pax: c.capacity_pax,
        enabled: !!existing,
        markup_pct: existing?.markup_pct || 0,
        price_override: existing?.price_override ?? null,
      };
    });
    setData({ ...catResp.data, transfers: transferRows, categories: activeCats });
  };
  useEffect(() => { load(); }, []);

  const toggleTour = (id, enabled) => {
    setData((d) => ({ ...d, tours: d.tours.map((x) => x.id === id ? { ...x, enabled } : x) }));
  };
  const setTourMarkup = (id, markup_pct) => {
    setData((d) => ({ ...d, tours: d.tours.map((x) => x.id === id ? { ...x, markup_pct: Number(markup_pct) || 0 } : x) }));
  };
  const setTourPriceOverride = (id, price_override) => {
    setData((d) => ({ ...d, tours: d.tours.map((x) => x.id === id ? { ...x, price_override: price_override === "" ? null : Number(price_override) } : x) }));
  };
  const toggleRoute = (id, enabled) => {
    setData((d) => ({ ...d, routes: d.routes.map((x) => x.id === id ? { ...x, enabled } : x) }));
  };
  const setRouteMarkup = (id, markup_pct) => {
    setData((d) => ({ ...d, routes: d.routes.map((x) => x.id === id ? { ...x, markup_pct: Number(markup_pct) || 0 } : x) }));
  };
  const setRoutePriceOverride = (id, price_override) => {
    setData((d) => ({ ...d, routes: d.routes.map((x) => x.id === id ? { ...x, price_override: price_override === "" ? null : Number(price_override) } : x) }));
  };
  // Iter 25c — transfers per category
  const toggleTransfer = (id, enabled) => {
    setData((d) => ({ ...d, transfers: (d.transfers || []).map((x) => x.id === id ? { ...x, enabled } : x) }));
  };
  const setTransferMarkup = (id, markup_pct) => {
    setData((d) => ({ ...d, transfers: (d.transfers || []).map((x) => x.id === id ? { ...x, markup_pct: Number(markup_pct) || 0 } : x) }));
  };
  // Iter 25c — tour price per category
  const setTourCategoryPrice = (tour_id, cat_key, price) => {
    setData((d) => ({
      ...d,
      tours: d.tours.map((t) => t.id === tour_id ? {
        ...t,
        category_prices: { ...(t.category_prices || {}), [cat_key]: price === "" ? null : Number(price) },
      } : t),
    }));
  };

  const openRulesEditor = (kind, item, displayName) => {
    setRulesEditor({ kind, id: item.id, name: displayName, rules: [...(item.seasonal_rules || [])] });
  };
  const saveRulesFromEditor = (rules) => {
    if (!rulesEditor) return;
    const { kind, id } = rulesEditor;
    const key = kind === "tour" ? "tours" : "routes";
    setData((d) => ({ ...d, [key]: d[key].map((x) => x.id === id ? { ...x, seasonal_rules: rules } : x) }));
    setRulesEditor(null);
  };

  const groupedTours = useMemo(() => {
    if (!data) return {};
    const g = {};
    for (const tr of data.tours) {
      const z = tr.zone || "—";
      (g[z] = g[z] || []).push(tr);
    }
    // Sort so partner zone comes first
    const ordered = Object.keys(g).sort((a, b) => {
      if (a === data.partner_zone) return -1;
      if (b === data.partner_zone) return 1;
      return a.localeCompare(b);
    });
    return { g, ordered };
  }, [data]);

  const saveAll = async () => {
    setSaving(true);
    try {
      const tours = data.tours.filter((t) => t.enabled).map((t) => {
        // Iter 25c: sanitiza category_prices — só valores > 0
        const cp = {};
        for (const [k, v] of Object.entries(t.category_prices || {})) {
          const n = Number(v);
          if (!isNaN(n) && n > 0) cp[k] = n;
        }
        return {
          id: t.id,
          markup_pct: Number(t.markup_pct) || 0,
          price_override: (t.price_override === null || t.price_override === undefined || t.price_override === "" || Number(t.price_override) <= 0) ? null : Number(t.price_override),
          category_prices: cp,
          seasonal_rules: (t.seasonal_rules || []).filter((r) => r.date_from && r.date_to).map((r) => ({
            name: r.name || "",
            date_from: r.date_from,
            date_to: r.date_to,
            mode: r.mode || "markup_pct",
            value: Number(r.value) || 0,
          })),
        };
      });
      // Iter 25c: rotas fixas sao geridas em /partner-portal/fixed-routes.
      // Preservamos as rotas ja gravadas para nao apagar seleccoes anteriores.
      const routes = (data.routes || []).filter((r) => r.enabled).map((r) => ({
        id: r.id,
        markup_pct: Number(r.markup_pct) || 0,
        price_override: (r.price_override === null || r.price_override === undefined || r.price_override === "" || Number(r.price_override) <= 0) ? null : Number(r.price_override),
        seasonal_rules: (r.seasonal_rules || []).filter((x) => x.date_from && x.date_to).map((x) => ({
          name: x.name || "",
          date_from: x.date_from,
          date_to: x.date_to,
          mode: x.mode || "markup_pct",
          value: Number(x.value) || 0,
        })),
      }));
      const transfers = (data.transfers || []).filter((t) => t.enabled).map((t) => ({
        id: t.id,   // category.key
        markup_pct: Number(t.markup_pct) || 0,
        price_override: null,  // dynamic pricing — override nao aplica
      }));
      await api.put("/partner/catalog", { tours, routes, transfers });
      toast.success(`${tours.length} tours · ${transfers.length} transferes guardados`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const updateBranding = async (payload) => {
    try {
      await api.patch("/partner/branding", payload);
      toast.success(t("toast_saved"));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadLogo = async (file) => {
    if (file.size > 512 * 1024) { toast.error("Logo deve ter menos de 512KB"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateBranding({ logo_url: up.url });
    } catch (e) {
      toast.error(`Erro ao enviar logo: ${formatApiError(e)}`);
    }
  };

  if (!data || !me) return <div className="p-10 text-slate-500">{t("client_loading")}</div>;

  const enabledTours = data.tours.filter((t) => t.enabled).length;
  const enabledRoutes = data.routes.filter((r) => r.enabled).length;
  const outOfZoneEnabled = data.tours.filter((t) => t.enabled && !t.zone_match).length;

  const query = q.trim().toLowerCase();
  const filterFn = (item) => {
    if (!query) return true;
    const pt = (item.translations || []).find((x) => x.lang === "PT") || {};
    return (item.slug || "").toLowerCase().includes(query)
      || (pt.title || "").toLowerCase().includes(query)
      || (item.zone || "").toLowerCase().includes(query)
      || (item.name || "").toLowerCase().includes(query);
  };

  return (
    <div className="p-10 max-w-[1400px]" data-testid="partner-catalog-page">
      <PortalPageHeader
        eyebrow="Vitrine"
        title="Catálogo"
        description="Escolha quais tours e transfers pretende oferecer e defina o preço final: margem %, preço fixo, ou regras sazonais (verão / Natal / etc.). Se deixar tudo vazio, o cliente vê o preço DiTours base."
      >
        <div className="flex items-center gap-2 border border-slate-300 bg-white px-3 h-10" data-testid="preview-date-block">
          <CalendarClock className="w-3.5 h-3.5 text-[#f59e0b]" />
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold whitespace-nowrap">Simular data</label>
          <input
            type="date"
            value={previewDate}
            onChange={(e) => setPreviewDate(e.target.value)}
            className="text-xs border-0 focus:outline-none text-[#0a0f1e] font-mono"
            data-testid="preview-date-input"
          />
          {previewDate && (
            <button type="button" onClick={() => setPreviewDate("")} className="text-slate-400 hover:text-red-600" data-testid="preview-date-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={saveAll}
          disabled={saving}
          data-testid="save-catalog"
          className="h-10 px-5 bg-[#f59e0b] text-[#0a0f1e] font-bold text-xs uppercase tracking-widest hover:bg-[#d97706] inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? t("action_saving") : t("integrations_save_all")}
        </button>
      </PortalPageHeader>

      {/* Branding + zone */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <div className="border border-slate-200 bg-white p-5" data-testid="branding-block">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Logotipo</p>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {me.logo_url ? (
                <img src={me.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <ImagePlus className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <label className="flex-1 text-xs cursor-pointer text-[#f59e0b] font-bold uppercase tracking-widest border border-[#f59e0b] hover:bg-[#f59e0b] hover:text-[#0a0f1e] px-3 py-2 inline-block text-center" data-testid="upload-logo-label">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="upload-logo-input" />
              {me.logo_url ? t("action_replace") : "Carregar"}
            </label>
          </div>
          <p className="text-[10px] text-slate-500 mt-2">PNG/JPG · máx. 512KB · fundo transparente aconselhado</p>
        </div>

        <div className="border border-slate-200 bg-white p-5" data-testid="zone-block">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Zona de operação</p>
          <div className="flex items-baseline gap-3">
            <div className="font-display text-3xl text-[#0a0f1e]" data-testid="partner-zone">{data.partner_zone}</div>
            <MapPin className="w-4 h-4 text-[#f59e0b]" />
          </div>
          <label className="mt-3 block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Código postal</span>
            <Input
              defaultValue={data.postal_code}
              placeholder="ex. 1200-234"
              onBlur={(e) => { if (e.target.value !== data.postal_code) updateBranding({ postal_code: e.target.value }); }}
              data-testid="postal-code-input"
              className="mt-1"
            />
          </label>
          <p className="text-[10px] text-slate-500 mt-2">A zona é calculada automaticamente a partir do CP.</p>
          <label className="mt-3 block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Antecedência mínima (horas)</span>
            <Input
              type="number" min="0" step="0.5"
              defaultValue={me?.min_advance_hours ?? 0}
              placeholder="0 = sem restrição"
              onBlur={(e) => updateBranding({ min_advance_hours: Number(e.target.value) || 0 })}
              data-testid="min-advance-input"
              className="mt-1 w-32"
            />
            <p className="text-[10px] text-slate-500 mt-1">Aplica a todas as suas reservas.</p>
          </label>
        </div>

        <div className="border border-slate-200 bg-gradient-to-br from-[#0a0f1e] to-[#1a1f3e] text-white p-5">
          <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-3">Resumo</p>
          <div className="space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-300">Tours ativos</span><span className="font-mono font-bold">{enabledTours}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-300">Rotas ativas</span><span className="font-mono font-bold">{enabledRoutes}</span></div>
            {outOfZoneEnabled > 0 && (
              <div className="flex justify-between text-sm text-amber-300 mt-3 pt-3 border-t border-white/10">
                <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Fora de zona</span>
                <span className="font-mono font-bold">{outOfZoneEnabled}</span>
              </div>
            )}
          </div>
          {outOfZoneEnabled > 0 && (
            <p className="text-[10px] text-slate-300 mt-3">Itens fora da sua zona serão vendidos ao cliente como <strong className="text-amber-300">motorista privado</strong> (hora + km) com pickup manual.</p>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="mb-5 flex items-center gap-2 max-w-md">
        <Search className="w-4 h-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar por nome, slug ou zona…" data-testid="catalog-search" />
      </div>

      {/* Tours grouped by zone */}
      <section className="mb-10">
        <h2 className="font-display text-xl text-[#0a0f1e] mb-4">Tours ({data.tours.length})</h2>
        {groupedTours.ordered.map((zone) => {
          const items = groupedTours.g[zone].filter(filterFn);
          if (items.length === 0) return null;
          const inZone = zone === data.partner_zone;
          return (
            <div key={zone} className="mb-6" data-testid={`zone-group-${zone}`}>
              <div className={`text-[10px] uppercase tracking-widest font-bold mb-2 flex items-center gap-2 ${inZone ? "text-emerald-700" : "text-amber-700"}`}>
                <MapPin className="w-3 h-3" /> {zone}
                {inZone && <span className="text-[9px] text-emerald-600 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5">A SUA ZONA · preço fixo</span>}
                {!inZone && zone !== "—" && <span className="text-[9px] text-amber-600 bg-amber-50 border border-amber-300 px-1.5 py-0.5">motorista privado · pickup manual</span>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {items.map((tour) => {
                  const pt = (tour.translations || []).find((x) => x.lang === "PT") || (tour.translations || [])[0] || {};
                  const hasOverride = tour.price_override && Number(tour.price_override) > 0;
                  const eff = computeEffective(tour, tour.price_base);
                  const client_price = eff.price.toFixed(0);
                  const previewLabel = previewDate ? (
                    eff.source === "seasonal_override" ? `Sazonal: ${eff.rule?.name || "?"} · fixo` :
                    eff.source === "seasonal_markup" ? `Sazonal: ${eff.rule?.name || "?"} · +${eff.rule?.value}%` :
                    eff.source === "override" ? "Preço fixo (override)" :
                    eff.source === "markup" ? `Base +${tour.markup_pct}%` :
                    "Preço DiTours"
                  ) : (
                    hasOverride ? "Preço fixo (override)" : (tour.markup_pct > 0 ? `Base +${tour.markup_pct}%` : "Preço DiTours")
                  );
                  const previewHighlight = previewDate && (eff.source === "seasonal_override" || eff.source === "seasonal_markup");
                  return (
                    <label key={tour.id} data-testid={`tour-card-${tour.id}`} className={`border p-3 flex flex-col gap-2 cursor-pointer transition-colors ${tour.enabled ? "border-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <input type="checkbox" checked={tour.enabled} onChange={(e) => toggleTour(tour.id, e.target.checked)} data-testid={`tour-toggle-${tour.id}`} className="mt-1 accent-[#f59e0b]" />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-[#0a0f1e]">{pt.title || tour.slug}</div>
                          <div className="text-[10px] text-slate-500">{tour.duration_hours}h · base €{tour.price_base}</div>
                        </div>
                      </div>
                      {tour.enabled && (
                        <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold w-14">Margem</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={tour.markup_pct || 0}
                              disabled={hasOverride}
                              onChange={(e) => setTourMarkup(tour.id, e.target.value)}
                              className="w-16 px-2 py-1 text-xs border border-slate-300 focus:border-[#f59e0b] focus:outline-none disabled:opacity-40"
                              data-testid={`tour-markup-${tour.id}`}
                            />
                            <span className="text-[11px] text-slate-500">%</span>
                            <span className="ml-auto text-[11px] font-mono text-slate-500" data-testid={`tour-effective-price-${tour.id}`}>base → €{client_price}</span>
                          </div>
                          {/* Msg 694: só mostra Cliente e Ganha; motorista/DiTours ocultos ao parceiro. */}
                          {(() => {
                            const admin = Number(tour.price_base) || 0;
                            const partnerPct = Number(tour.markup_pct) || 0;
                            const client = Number(client_price) || 0;
                            const partnerEarn = client * partnerPct / 100;
                            const minClient = admin > 0 && partnerPct < 100 ? admin / (1 - partnerPct / 100) : admin;
                            const belowMin = admin > 0 && client < minClient - 0.5;
                            return (
                              <div className={`text-[10px] font-mono flex items-center gap-2 -mt-1 ${belowMin ? "text-red-600" : "text-slate-500"}`}>
                                <span className={`font-bold ${belowMin ? "text-red-600" : "text-[#0a0f1e]"}`}>Cliente €{client.toFixed(0)}</span>
                                <span className="text-slate-300">·</span>
                                <span className={`font-bold ${belowMin ? "text-red-500" : "text-emerald-700"}`}>Ganha €{partnerEarn.toFixed(0)}</span>
                                {belowMin && <span className="text-red-600 font-bold not-italic normal-case">↑ mín €{minClient.toFixed(0)}</span>}
                              </div>
                            );
                          })()}

                          {/* Iter 25c — precos por categoria */}
                          {(tour.vehicle_pricing || []).length > 0 && (
                            <div className="pt-1 border-t border-dashed border-slate-100">
                              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Preço por categoria €</div>
                              <div className="grid grid-cols-2 gap-1">
                                {(tour.vehicle_pricing || []).map((vp) => {
                                  const cat = (data.categories || []).find((c) => c.key === vp.category_key);
                                  const label = cat?.label || vp.category_key;
                                  const partnerPrice = (tour.category_prices || {})[vp.category_key];
                                  return (
                                    <div key={vp.category_key} className="flex items-center gap-1 text-[10px]">
                                      <span className="text-slate-600 flex-1 truncate">{label}</span>
                                      <span className="text-slate-400 font-mono">€{vp.price_eur}</span>
                                      <input
                                        type="number" min="0" step="1"
                                        placeholder={vp.price_eur}
                                        value={partnerPrice ?? ""}
                                        onChange={(e) => setTourCategoryPrice(tour.id, vp.category_key, e.target.value)}
                                        data-testid={`tour-cat-${tour.id}-${vp.category_key}`}
                                        className="w-14 px-1 py-0.5 border border-slate-300 focus:border-[#f59e0b] focus:outline-none text-[10px]"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <div className={`text-[9px] uppercase tracking-widest ${previewHighlight ? "text-emerald-700 font-bold" : "text-slate-400"}`} data-testid={`tour-price-source-${tour.id}`}>
                              {previewLabel}
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); openRulesEditor("tour", tour, pt.title || tour.slug); }}
                              data-testid={`tour-rules-${tour.id}`}
                              className={`text-[9px] uppercase tracking-widest font-bold inline-flex items-center gap-1 px-1.5 py-0.5 border ${(tour.seasonal_rules || []).length > 0 ? "text-[#f59e0b] border-[#f59e0b]" : "text-slate-500 border-slate-300 hover:text-[#f59e0b] hover:border-[#f59e0b]"}`}
                            >
                              <CalendarClock className="w-2.5 h-2.5" /> {(tour.seasonal_rules || []).length > 0 ? `${(tour.seasonal_rules).length} ${(tour.seasonal_rules).length === 1 ? "regra" : "regras"}` : "Sazonal"}
                            </button>
                          </div>
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {/* Iter 25c: Fixed routes section removed — parceiro configura precos em /partner-portal/fixed-routes */}

      {rulesEditor && (
        <SeasonalRulesModal
          initial={rulesEditor}
          onClose={() => setRulesEditor(null)}
          onSave={saveRulesFromEditor}
        />
      )}

      {/* Iter 25 Sub-Fase D — Transferes dinâmicos por categoria */}
      <section className="mt-8" data-testid="transfers-section">
        <div className="flex items-baseline gap-3 mb-3">
          <h3 className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">Transferes dinâmicos</h3>
          <span className="text-[10px] text-slate-500">Preço = base + km × per_km + min × per_min. A sua margem % soma no total ao cliente.</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {(data.transfers || []).map((tr) => (
            <label key={tr.id} data-testid={`transfer-card-${tr.id}`} className={`border p-4 flex flex-col gap-3 cursor-pointer transition-colors ${tr.enabled ? "border-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200 bg-white hover:border-slate-400"}`}>
              <div className="flex items-start justify-between gap-2">
                <input
                  type="checkbox"
                  checked={tr.enabled}
                  onChange={(e) => toggleTransfer(tr.id, e.target.checked)}
                  data-testid={`transfer-toggle-${tr.id}`}
                  className="mt-1 accent-[#f59e0b]"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#0a0f1e]">{tr.label}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {tr.capacity_pax}p · €{tr.base_price_eur} base · €{tr.price_per_km}/km · €{tr.price_per_minute}/min
                  </div>
                </div>
              </div>
              {tr.enabled && (
                <div className="pt-2 border-t border-slate-100 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold w-14">Margem</span>
                  <input
                    type="number" min="0" max="100" step="1"
                    value={tr.markup_pct || 0}
                    onChange={(e) => setTransferMarkup(tr.id, e.target.value)}
                    className="w-16 px-2 py-1 text-xs border border-slate-300 focus:border-[#f59e0b] focus:outline-none"
                    data-testid={`transfer-markup-${tr.id}`}
                  />
                  <span className="text-[11px] text-slate-500">%</span>
                  <span className="ml-auto text-[10px] text-slate-500">
                    Ex: 10km × €{tr.price_per_km} + €{tr.base_price_eur} = <strong className="text-[#0a0f1e]">€{(tr.base_price_eur + 10 * tr.price_per_km).toFixed(0)}</strong>
                    {tr.markup_pct > 0 && <span className="text-[#f59e0b] font-bold"> → €{((tr.base_price_eur + 10 * tr.price_per_km) * (1 + tr.markup_pct / 100)).toFixed(0)}</span>}
                  </span>
                </div>
              )}
            </label>
          ))}
        </div>
        {(data.transfers || []).length === 0 && (
          <div className="text-center text-sm text-slate-500 py-8 border border-dashed border-slate-300">
            Sem categorias de veículo disponíveis. Contacte o administrador.
          </div>
        )}
      </section>
    </div>
  );
}

function SeasonalRulesModal({ initial, onClose, onSave }) {
  const [rules, setRules] = useState(initial.rules || []);
  const addRule = () => setRules([...rules, { name: "", date_from: "", date_to: "", mode: "markup_pct", value: 0 }]);
  const removeRule = (idx) => setRules(rules.filter((_, i) => i !== idx));
  const updateRule = (idx, patch) => setRules(rules.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const save = () => {
    // Validate: each row must have from & to; from <= to
    for (const r of rules) {
      if (!r.date_from || !r.date_to) { toast.error("Cada regra precisa de data início e fim"); return; }
      if (r.date_from > r.date_to) { toast.error("Data início não pode ser depois de fim"); return; }
    }
    onSave(rules);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="seasonal-modal">
      <div className="bg-white border border-[#f59e0b]/30 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold">Regras sazonais</div>
            <h3 className="font-display text-xl text-[#0a0f1e] mt-0.5">{initial.name}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-[#0a0f1e]" data-testid="seasonal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          <p className="text-xs text-slate-600 mb-4">
            Defina preços diferentes por período. A <strong>primeira regra correspondente</strong> à data do serviço é aplicada.
            Fora de qualquer período, aplica-se o preço permanente (override ou margem).
          </p>

          {rules.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-6 border border-dashed border-slate-300">
              Sem regras. Clique em <strong>+ Adicionar regra</strong> para criar (ex.: Verão +20%, Natal €150).
            </div>
          )}

          <div className="space-y-3">
            {rules.map((r, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 border border-slate-200" data-testid={`rule-row-${idx}`}>
                <div className="col-span-3">
                  <label className="text-[9px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Nome</label>
                  <input value={r.name || ""} onChange={(e) => updateRule(idx, { name: e.target.value })} placeholder="Verão" className="w-full px-2 py-1.5 text-xs border border-slate-300 focus:border-[#f59e0b] focus:outline-none" data-testid={`rule-name-${idx}`} />
                </div>
                <div className="col-span-2">
                  <label className="text-[9px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Início</label>
                  <input type="date" value={r.date_from || ""} onChange={(e) => updateRule(idx, { date_from: e.target.value })} className="w-full px-2 py-1.5 text-xs border border-slate-300 focus:border-[#f59e0b] focus:outline-none" data-testid={`rule-from-${idx}`} />
                </div>
                <div className="col-span-2">
                  <label className="text-[9px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Fim</label>
                  <input type="date" value={r.date_to || ""} onChange={(e) => updateRule(idx, { date_to: e.target.value })} className="w-full px-2 py-1.5 text-xs border border-slate-300 focus:border-[#f59e0b] focus:outline-none" data-testid={`rule-to-${idx}`} />
                </div>
                <div className="col-span-2">
                  <label className="text-[9px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Tipo</label>
                  <select value={r.mode || "markup_pct"} onChange={(e) => updateRule(idx, { mode: e.target.value })} className="w-full px-2 py-1.5 text-xs border border-slate-300 focus:border-[#f59e0b] focus:outline-none bg-white" data-testid={`rule-mode-${idx}`}>
                    <option value="markup_pct">Margem %</option>
                    <option value="price_override">Preço €</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-[9px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Valor</label>
                  <input type="number" min="0" step="1" value={r.value ?? 0} onChange={(e) => updateRule(idx, { value: Number(e.target.value) || 0 })} className="w-full px-2 py-1.5 text-xs border border-slate-300 focus:border-[#f59e0b] focus:outline-none" data-testid={`rule-value-${idx}`} />
                </div>
                <div className="col-span-1 flex justify-end">
                  <button onClick={() => removeRule(idx)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`rule-remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={addRule}
            className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#f59e0b] hover:text-[#d97706] font-bold border border-[#f59e0b] hover:bg-[#f59e0b]/5 px-3 py-2"
            data-testid="add-rule"
          >
            <Plus className="w-3.5 h-3.5" /> Adicionar regra
          </button>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="text-xs uppercase tracking-widest text-slate-600 hover:text-[#0a0f1e] font-bold px-4 py-2">Cancelar</button>
          <button
            onClick={save}
            className="text-xs uppercase tracking-widest bg-[#f59e0b] text-[#0a0f1e] font-bold hover:bg-[#d97706] px-5 py-2 inline-flex items-center gap-2"
            data-testid="seasonal-save"
          >
            <Save className="w-3.5 h-3.5" /> Guardar regras
          </button>
        </div>
      </div>
    </div>
  );
}
